/**
 * StickyAskBar
 *
 * "Ask JAI about this [strategy/draft/review/waiver page]" — pinned to the
 * bottom of the viewport, following the scroll. Streams against
 * POST /api/leagues/:id/draft/chat with { scope, strategyId, extraContext }
 * via streamFetch. Shared across every tool that wants a follow-up ask bar —
 * not Draft-Board-specific despite the endpoint's name (that endpoint
 * already branches on `scope` server-side rather than assuming draft
 * context; see draftChat in draftBoardController.js). Only `scope`/the id
 * prop, `context` and the placeholder text differ per caller.
 *
 * Props:
 *   leagueId
 *   scope        — free-form string identifying the calling tool, e.g.
 *                  'strategy' | 'live' | 'review' | 'waiver'. The backend
 *                  only has special-cased context lookups for a couple of
 *                  these; anything else still gets a real answer, just
 *                  without that extra context unless `context` is passed.
 *   scopeId       — the strategyId/draftResultId this question is about,
 *                   when the backend can look up context by id (optional)
 *   context       — a plain-text context block the CALLER already has in
 *                   state and the backend has no id to look up (e.g. Waiver
 *                   Wire's active filters + returned players) — sent as
 *                   `extraContext`, appended server-side regardless of scope
 *   placeholder   — input placeholder text
 */

import { useState, useRef, useEffect } from 'react'
import { streamFetch } from '../services/api'
import { JAI_FONTS } from './JaiBrand'
import { V2 } from './JaiV2'
import { JAIMascot } from './JAIMascot'
import JaiRichText from './JaiRichText'
import { useContentOffset } from '../context/SidebarContext'
import { IS_NATIVE_APP } from '../services/runtime'

async function consumeStream(response, onText) {
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop()
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      try {
        const data = JSON.parse(line.slice(6))
        if (data.done) return
        if (data.text) onText(data.text)
      } catch { /* ignore a partial/malformed chunk */ }
    }
  }
}

export default function StickyAskBar({ leagueId, scope, scopeId, context, placeholder = 'Ask JAI about this' }) {
  const contentOffset = useContentOffset()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState([]) // [{role, content}]
  const [streaming, setStreaming] = useState(false)
  const bottomRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const send = async () => {
    const content = input.trim()
    if (!content || streaming) return
    setInput('')
    setOpen(true)
    const next = [...messages, { role: 'user', content }]
    setMessages([...next, { role: 'assistant', content: '' }])
    setStreaming(true)

    try {
      const response = await streamFetch(`/api/leagues/${leagueId}/draft/chat`, {
        method: 'POST',
        body: JSON.stringify({ messages: next.slice(-20), scope, strategyId: scopeId, extraContext: context || undefined }),
      })
      // Guest out of free questions: the sign-up pop-up is opening (see
      // streamFetch). Drop the empty answer bubble and keep their question.
      if (response.guestLimitHit || response.creditsExhausted) {
        setMessages(messages)
        setInput(content)
        setOpen(false)
        return
      }
      await consumeStream(response, (chunk) => {
        setMessages((prev) => {
          const copy = [...prev]
          copy[copy.length - 1] = { role: 'assistant', content: copy[copy.length - 1].content + chunk }
          return copy
        })
      })
    } catch {
      setMessages((prev) => {
        const copy = [...prev]
        copy[copy.length - 1] = { role: 'assistant', content: 'Something went wrong answering that — try again.' }
        return copy
      })
    } finally {
      setStreaming(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', left: contentOffset, right: 0, bottom: 0, zIndex: 40,
      display: 'flex', justifyContent: 'center', pointerEvents: 'none',
      transition: 'left 220ms cubic-bezier(0.4,0,0.2,1)',
      // Keeps the composer clear of the home indicator — bottom: 0 above is
      // the true edge of the screen, so this pads the fixed box itself
      // rather than the pill inside it (which still gets its own 16px
      // margin on top of this). No-op on web. On native, also clears the
      // bottom tab bar (AppShell.jsx renders it below every tool page this
      // composer appears on) — the tab bar's own safe-bottom padding means
      // its true height is 48px + var(--safe-bottom), so that's the extra
      // offset needed on top of this bar's own safe-bottom padding.
      paddingBottom: IS_NATIVE_APP ? 'calc(var(--safe-bottom) + 48px)' : 'var(--safe-bottom)',
    }}>
      <div style={{ width: '100%', maxWidth: 640, margin: '0 16px 16px', pointerEvents: 'auto' }}>
        {open && messages.length > 0 && (
          <div style={{
            background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 16,
            boxShadow: V2.composerShadow, marginBottom: 8, maxHeight: 320, display: 'flex', flexDirection: 'column',
          }}>
            {/* Minimize — collapses back to just the input pill without
                losing the conversation; focusing the input reopens it. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 10px 0', flexShrink: 0 }}>
              <button
                onClick={() => setOpen(false)}
                aria-label="Minimize"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'transparent', color: V2.mute, cursor: 'pointer' }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3.5 6.5 8 11l4.5-4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div style={{ overflowY: 'auto', padding: '0 16px 14px', WebkitOverflowScrolling: 'touch' }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-start' }}>
                {m.role === 'assistant' && <JAIMascot size={22} state="idle" animate={false} />}
                <div style={{
                  marginLeft: m.role === 'user' ? 'auto' : 0,
                  borderLeft: m.role === 'user' ? '3px solid rgba(30,91,214,0.7)' : 'none',
                  padding: m.role === 'user' ? '2px 0 2px 11px' : 0, borderRadius: 0,
                  maxWidth: '85%',
                }}>
                  {/* User turns are plain text (whatever they typed); assistant
                      turns go through JaiRichText so bold text and bullets
                      render properly and [ACTION:x] routing markers never
                      leak as literal text — both were showing raw before. */}
                  {m.role === 'user' ? (
                    <span style={{ fontSize: 13, lineHeight: 1.55, color: V2.navy, whiteSpace: 'pre-wrap' }}>{m.content}</span>
                  ) : (
                    m.content
                      ? <JaiRichText content={m.content} style={{ fontSize: 13, lineHeight: 1.55 }} />
                      : (streaming && i === messages.length - 1
                          ? <span style={{ fontSize: 13, lineHeight: 1.55, color: V2.navy }}>…</span>
                          : null)
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
            </div>
          </div>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 999,
          boxShadow: V2.composerShadow, padding: '8px 8px 8px 16px',
        }}>
          <JAIMascot size={22} state={streaming ? 'thinking' : 'idle'} animate={streaming} />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            onFocus={() => messages.length > 0 && setOpen(true)}
            placeholder={placeholder}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13.5, fontFamily: JAI_FONTS.tight, color: V2.navy, background: 'transparent' }}
          />
          <button
            onClick={send}
            disabled={!input.trim() || streaming}
            style={{
              flexShrink: 0, width: 32, height: 32, borderRadius: '50%', border: 'none',
              background: input.trim() && !streaming ? V2.blue : 'rgba(10,31,68,0.12)',
              color: 'white', cursor: input.trim() && !streaming ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
